import clsx from "clsx"
import { type DetailedHTMLProps, forwardRef, type HTMLAttributes } from "react"
import { Controller, type FieldValues } from "react-hook-form"
import type { ButtonColor, CompoundedComponent, IFormControl } from "@/shared/types"
import "./style.scss"

type InputCheck = Omit<DetailedHTMLProps<HTMLAttributes<HTMLInputElement>, HTMLInputElement>, "onChange">

export interface SwitchProps extends InputCheck {
  type?: "checkbox" | "radio" | "switch"
  label?: string
  onChange?: (value: boolean) => void
  checked?: boolean
  rtl?: boolean
  color?: ButtonColor
  error?: string
  name?: string
  disabled?: boolean
}

const SWITCH_COLORS = {
  primary: "bg-blue-600 border-blue-600",
  success: "bg-green-600 border-green-600",
  error: "bg-red-600 border-red-600",
  warning: "bg-yellow-600 border-yellow-600",
  info: "bg-blue-600 border-blue-600",
  inherit: "bg-slate-700 border-slate-700",
  second: "bg--800 border-slate-800"
}
export const Switch = forwardRef<HTMLInputElement, SwitchProps>(({ label, onChange, type = "switch", checked, color, error, ...props }, ref) => {
  return (
    <label className={clsx("switch", `switch-type--${type}`, `switch-color--${color}`)}>
      <input {...props} type={type === "radio" ? "radio" : "checkbox"} checked={checked} onChange={e => onChange?.(e.target.checked)} />
      {label != null && <span>{label}</span>}
      {error != null && <p>{error}</p>}
    </label>
  )
}) as CompoundedComponent<SwitchProps, HTMLInputElement, { Form: typeof FormSwitch }>

const FormSwitch = <T extends FieldValues>({ control, name, rules, required, ...props }: IFormControl<T> & SwitchProps) => {
  return (
    <Controller
      {...{
        control,
        name,
        rules: { required: { value: required ?? false, message: "this-field-is-required" }, ...rules }
      }}
      render={({ field, fieldState: { error } }) => (
        <Switch
          {...props}
          {...field}
          onChange={e => {
            field.onChange(e)
            props.onChange?.(e)
          }}
          error={error?.message}
          checked={field.value ?? false}
        />
      )}
    />
  )
}

Switch.Form = FormSwitch
